import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { TenantLink } from '@/components/ui/TenantLink';
import type { AnnouncementResponse } from '@/store/api/endpoints/client/announcementsApi';
import { cn } from '@/lib/utils';
import { announcementTypeMeta } from '@/components/features/announcements/announcement-meta';
import {
  buildAnnouncementsPagePath,
  ISSUE_MAINTENANCE_BANNER_HEIGHT_PX,
} from '@/modules/announcements/constants';

export interface IssueMaintenanceBannerProps {
  announcement: AnnouncementResponse;
  topOffsetPx: number;
  onDismiss: () => void;
}

export function IssueMaintenanceBanner({
  announcement,
  topOffsetPx,
  onDismiss,
}: IssueMaintenanceBannerProps) {
  const { t } = useTranslation('announcements', { keyPrefix: 'banner' });
  const isMaintenance = announcement.type === 'Maintenance';
  const readLabel = isMaintenance ? t('moreInfo') : t('readIssue');
  const typeMeta =
    announcement.type === 'Issue' || announcement.type === 'Maintenance'
      ? announcementTypeMeta[announcement.type]
      : null;

  const colors = isMaintenance
    ? {
        banner: 'border-yellow-200 bg-yellow-50',
        text: 'text-yellow-950',
        dismiss: 'text-yellow-800 hover:bg-yellow-100',
      }
    : {
        banner: 'border-red-200 bg-red-50',
        text: 'text-red-950',
        dismiss: 'text-red-800 hover:bg-red-100',
      };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        height: `${ISSUE_MAINTENANCE_BANNER_HEIGHT_PX}px`,
        top: `${topOffsetPx}px`,
      }}
      className={cn(
        'animate-in slide-in-from-top fade-in-0 duration-300 ease-out fixed inset-x-0 z-39 flex items-center border-b px-4 shadow-sm',
        colors.banner,
      )}
    >
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-[7.5rem]">
        <div className="flex min-w-0 max-w-full items-center justify-center gap-2">
          {typeMeta && (
            <span
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full',
                typeMeta.avatarClassName,
              )}
              aria-hidden="true"
            >
              <i className={cn('pi text-xs', typeMeta.icon)} />
            </span>
          )}
          <p
            title={announcement.title}
            className={cn('min-w-0 truncate text-sm font-medium', colors.text)}
          >
            {announcement.title}
          </p>
        </div>
      </div>

      <div className="relative z-10 ml-auto flex shrink-0 items-center gap-2">
        <Button asChild variant="outline" size="sm" className="h-7 bg-white/80">
          <TenantLink
            to={buildAnnouncementsPagePath({
              type: isMaintenance ? 'Maintenance' : 'Issue',
              id: announcement.id,
            })}
          >
            {readLabel}
          </TenantLink>
        </Button>
        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            'flex size-7 items-center justify-center rounded-md transition-colors',
            colors.dismiss,
          )}
          aria-label={t('dismiss')}
        >
          <i className="pi pi-times text-sm" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
